import React, { FC, useEffect, useState } from "react";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { TextField } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import { OrganizationSettingsFormValues } from "../../OrganizationSettings.types";

import { Box, Button, Stack, IconButton, MenuItem } from "@components/base";
import { useGetRevenueAndExpenses } from "@api";
import { useUserStore } from "@store/user";

interface RevenueAndExpensesSettings {
  form: UseFormReturn<OrganizationSettingsFormValues>;
  companyId: string;
}

const RevenueAndExpensesSettings: FC<RevenueAndExpensesSettings> = ({
  form: { control, setValue },
  companyId,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { revenueAndExpenses, revenueAndExpensesEnabled } = useGetRevenueAndExpenses(
    user,
    {
      companyId,
    },
  );

  const yearsFilter = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;

    return year.toString();
  });
  const [availableYears, setAvailableYears] = useState<string[]>(yearsFilter);
  const { fields, append, remove } = useFieldArray({
    control,
    name: "revenueAndExpenses",
  });

  const updateYears = (selectedYear: string, previousYear?: string): void => {
    setAvailableYears((prevYears) => {
      let updatedYears = [...prevYears];

      if (selectedYear) {
        updatedYears = updatedYears.filter((year) => year !== selectedYear);
      }

      if (previousYear && !updatedYears.includes(previousYear)) {
        updatedYears.push(previousYear);
      }

      return updatedYears.sort((a, b) => parseInt(b) - parseInt(a));
    });
  };

  // TODO: submit changes on save
  const handleAddRevenueAndExpense = (index: string): void => {
    append({
      id: index,
      year: "",
      revenue: null,
      opex: null,
      capex: null,
    });
  };

  useEffect(() => {
    if (revenueAndExpenses) {
      setValue("revenueAndExpenses", revenueAndExpenses ?? []);

      const usedYears = revenueAndExpenses.map((item) => item.year);

      setAvailableYears((prevYears) =>
        prevYears.filter((year) => !usedYears.includes(year)),
      );
    }
  }, [revenueAndExpenses]);

  return (
    revenueAndExpensesEnabled && (
      <Stack gap={2} direction="column">
        {fields.map((item, index) => {
          return (
            <Stack key={item.id} gap={2} direction="row" justifyItems="center">
              <Controller
                control={control}
                name={`revenueAndExpenses.${index}.year`}
                rules={{ required: t("validation.yearIsRequired") }}
                render={({ field, fieldState: { error } }): React.ReactElement => (
                  <TextField
                    {...field}
                    select
                    label={t("common.year")}
                    variant="outlined"
                    helperText={error?.message}
                    error={Boolean(error)}
                    fullWidth
                    onChange={(e) => {
                      const selectedYear = e.target.value;

                      field.onChange(selectedYear);

                      updateYears(selectedYear, field.value);
                    }}
                  >
                    {availableYears
                      .concat(field.value ? [field.value] : [])
                      .map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name={`revenueAndExpenses.${index}.revenue`}
                render={({ field, fieldState: { error } }): React.ReactElement => (
                  <TextField
                    {...field}
                    label={t("common.revenue")}
                    variant="outlined"
                    helperText={error?.message}
                    fullWidth
                    error={Boolean(error)}
                  ></TextField>
                )}
              />
              <Controller
                control={control}
                name={`revenueAndExpenses.${index}.opex`}
                render={({ field, fieldState: { error } }): React.ReactElement => (
                  <TextField
                    {...field}
                    label="OPEX"
                    variant="outlined"
                    helperText={error?.message}
                    fullWidth
                    error={Boolean(error)}
                  ></TextField>
                )}
              />
              <Controller
                control={control}
                name={`revenueAndExpenses.${index}.capex`}
                render={({ field, fieldState: { error } }): React.ReactElement => (
                  <TextField
                    {...field}
                    label="CAPEX"
                    variant="outlined"
                    helperText={error?.message}
                    fullWidth
                    error={Boolean(error)}
                  ></TextField>
                )}
              />
              <IconButton
                aria-label="minus"
                onClick={() => {
                  remove(index);
                  updateYears("", item.year);
                }}
                color="error"
              >
                <FeatherIcon icon="minus" size={20} />
              </IconButton>
            </Stack>
          );
        })}

        <Box>
          <Button
            onClick={() => handleAddRevenueAndExpense(Date.now().toString())}
            variant="text"
            size="small"
          >
            <FeatherIcon icon="plus" size={20} />
            {t("settings.addRecord")}
          </Button>
        </Box>
      </Stack>
    )
  );
};

export default RevenueAndExpensesSettings;
